import { listLocations } from '@koala/sdk/v4';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { getLocationName } from './utils';
import { createHttpClient } from '@/services/client';
import { getOrigin } from '@/utils';
import { isRetryableError } from '@/utils/networkErrors';
import { withJsonGuard } from '@/utils/withJsonGuard';

// Same budget as the menu query: 2 retries on top of the first attempt, network and 5xx only.
const MAX_RETRIES = 2;

/**
 * Every store of the brand, for the Select Location pop-up (TEAM-13315). Called
 * from the pop-up's contents, which only mount while it is open, so nothing is
 * fetched until a diner opens it. React Query rather than the `fetchAllLocations`
 * saga: that saga drives the finder's view state, toggles `locations.loading`
 * (which CartReconciliation watches) and re-defaults the finder's active location.
 */
export function useOrgLocations() {
  // The route pattern (e.g. `/store/[...id]`, `/checkout`), for the non-JSON report's tag.
  const { pathname } = useRouter();

  return useQuery({
    queryKey: ['locations', 'all'],
    queryFn: async () => {
      const client = createHttpClient({ origin: getOrigin(window.location.host) });
      const response = await withJsonGuard(() => listLocations({ paginate: false }, { client }), {
        route: pathname,
        urlHint: '/store-locations',
      });

      return [...response.data].sort((a, b) =>
        getLocationName(a).localeCompare(getLocationName(b)),
      );
    },
    refetchOnWindowFocus: false,
    staleTime: 3600000, // 60 minutes
    retry: (failureCount, error) => failureCount < MAX_RETRIES && isRetryableError(error),
  });
}
